import React, { useState } from "react";
import { Box, Typography, InputBase, Stack } from "@mui/material";
import { motion } from "framer-motion";
import { Search, X, Sparkles, BookOpen, Users, Award } from "lucide-react";
import LogoMarquee from "../../components/ui/LogoMarquee";
import { fadeUp } from "../../utils/animations";
import heroBg from "./studentbackground.png";

const HERO_IMAGE_URL = heroBg;

const categories = [
  "Cloud",
  "Security",
  "Networking",
  "DevOps",
  "Linux",
  "Data Analytics",
  "AI",
  "AWS",
  "Azure",
  "Project Management",
];

const stats = [
  { value: "300+", label: "Courses live", icon: BookOpen },
  { value: "40K+", label: "Learners enrolled", icon: Users },
  { value: "60+", label: "Certification tracks", icon: Award },
];

const accreditations = [
  { name: "NAICU", src: "/logo/NAICU.png" },
  { name: "PMP", src: "/logo/PMP.png" },
  { name: "AAHEA", src: "/logo/AAHEA.png" },
  { name: "GARP", src: "/logo/GARP.jpg" },
  { name: "The Agile Alliance", src: "/logo/AgileAlliance.jpg" },
  { name: "Six Sigma Council", src: "/logo/SixSigmaCouncil.jpg" },
];

export default function LearnHero({ onSearch, onCategory }) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState("");

  const handleSearch = (val) => {
    setQuery(val);
    onSearch?.(val);
  };

  const handleCategory = (cat) => {
    const next = cat === active ? "" : cat;
    setActive(next);
    onCategory?.(next);
  };

  return (
    <Box component="section" sx={{ bgcolor: "#FAFBFF", position: "relative", overflow: "hidden" }}>
      {/* ============ HERO — background image + directional scrim, matches Gateway ============ */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 12, md: 16 },
          pb: { xs: 14, md: 18 },
          overflow: "hidden",
        }}
      >
        {/* Background photograph */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${HERO_IMAGE_URL})`,
            backgroundSize: "cover",
            backgroundPosition: { xs: "right center", md: "center 28%" },
            transform: { xs: "none", md: "scale(1.04)" },
          }}
        />

        {/* Directional gradient scrim */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(100deg, rgba(6,12,32,0.94) 0%, rgba(8,16,42,0.87) 32%, rgba(11,21,48,0.55) 58%, rgba(11,21,48,0.26) 76%, rgba(11,21,48,0.12) 100%)",
          }}
        />

        {/* Fade into page background at bottom */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: { xs: 90, md: 120 },
            background: "linear-gradient(180deg, rgba(250,251,255,0) 0%, #FAFBFF 100%)",
          }}
        />

        {/* Fine blueprint grid over the scrim side */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "linear-gradient(100deg, black 0%, black 35%, transparent 65%)",
            WebkitMaskImage: "linear-gradient(100deg, black 0%, black 35%, transparent 65%)",
          }}
        />

        {/* Ambient glow */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            left: "6%",
            width: 640,
            height: 460,
            background: "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(37,99,235,0.32), transparent 70%)",
            pointerEvents: "none",
            filter: "blur(10px)",
          }}
        />

               <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
          <Box sx={{ maxWidth: 620 }}>
            <motion.div {...fadeUp(0)}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.75,
                  py: 0.6,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.22)",
                  bgcolor: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(8px)",
                  mb: 2.75,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#5B9BFF" }} />
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    color: "#BFD4FF",
                    textTransform: "uppercase",
                  }}
                >
                  Professional course marketplace
                </Typography>
              </Box>
            </motion.div>

            <motion.div {...fadeUp(0.08)}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.2rem" },
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                  color: "#FFFFFF",
                  textShadow: "0 2px 24px rgba(0,0,0,0.35)",
                  mb: 2,
                }}
              >
                Learn, certify, and move{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(90deg, #7CB2FF 0%, #A9C9FF 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  faster in tech.
                </Box>
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.14)}>
              <Typography
                sx={{
                  fontSize: { xs: 14.5, md: 16 },
                  color: "rgba(226,232,255,0.86)",
                  lineHeight: 1.75,
                  maxWidth: 520,
                  mb: 4,
                }}
              >
                Search, filter, compare, and follow recommended career paths across AWS, Azure,
                cyber security, networking, Linux, DevOps, AI, and data.
              </Typography>
            </motion.div>

           

            {/* Stat strip — glass panel, matches Gateway */}
            <motion.div {...fadeUp(0.32)}>
             
          <Box
  sx={{
    display: "inline-flex",
    flexWrap: "wrap",
    gap: { xs: 3, md: 5 },
    rowGap: 2,
    mt: 2,
    px: { xs: 2.5, md: 3.5 },
    py: { xs: 2, md: 2.25 },
    borderRadius: "16px",
    border: "1px solid rgba(255,255,255,0.28)",       // was 0.16 — stronger border
    bgcolor: "rgba(255,255,255,0.12)",                  // was 0.06 — brighter fill
    backdropFilter: "blur(14px)",
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.35)",     // new — lifts it off the image
  }}
>
                {stats.map((s) => {
                  const Icon = s.icon;
                  return (
                    <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1.25, textAlign: "left" }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(255,255,255,0.1)",
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={16} color="#A9C9FF" strokeWidth={2} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: { xs: "1.25rem", md: "1.45rem" },
                            fontWeight: 800,
                            color: "#FFFFFF",
                            letterSpacing: "-0.02em",
                            lineHeight: 1,
                          }}
                        >
                          {s.value}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "rgba(226,232,255,0.75)", mt: 0.4, fontWeight: 600 }}>
                          {s.label}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </motion.div>
          </Box>
        </Box>
      </Box>

      {/* ============ Trusted training partners — mirrors Gateway ============ */}
      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
        <motion.div {...fadeUp(0.1)}>
          <Box sx={{ textAlign: "center", pt: { xs: 4, md: 5 }, pb: 7 }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}  sx={{
    mb: 2.5,
    width: "100%",
    justifyContent: "center"}}>
              <Sparkles size={13} color="#94A3B8" />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  color: "#94A3B8",
                  textTransform: "uppercase",
                }}
              >
                Trusted training partners
              </Typography>
            </Stack>
            <LogoMarquee />
          </Box>
        </motion.div>
      </Box>

      {/* ============ Accreditations — mirrors Gateway ============ */}
      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
        <motion.div {...fadeUp(0.14)}>
          <Box sx={{ textAlign: "center", pb: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}  sx={{
    mb: 2.5,
    width: "100%",
    justifyContent: "center",}}>
              <Sparkles size={13} color="#94A3B8" />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  color: "#94A3B8",
                  textTransform: "uppercase",
                }}
              >
                Accreditations
              </Typography>
            </Stack>

            <Typography sx={{ fontSize: { xs: 12.5, md: 13.5 }, color: "#94A3B8", mb: 4, fontWeight: 500 }}>
              Recognized and endorsed by leading global bodies
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(6, 1fr)" },
                gap: { xs: 1.5, md: 2 },
              }}
            >
              {accreditations.map((item) => (
                <Box
                  key={item.name}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.25,
                    height: { xs: 96, md: 108 },
                    px: 2,
                    borderRadius: "14px",
                    bgcolor: "#FFFFFF",
                    border: "1px solid rgba(15,23,42,0.08)",
                    boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
                    transition: "transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 14px 28px -14px rgba(37,99,235,0.35)",
                      borderColor: "rgba(37,99,235,0.28)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={item.src}
                    alt={item.name}
                    sx={{ height: { xs: 32, md: 36 }, width: { xs: 32, md: 36 }, objectFit: "contain" }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: 10.5, md: 11.5 },
                      fontWeight: 600,
                      color: "#64748B",
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {item.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}